import * as THREE from 'three'
import { gsap } from 'gsap'
import Experience from '../Experience.js'

// Ouverture de la visite : écran d'accueil, bouton « Démarrer le parcours »,
// descente en spirale à travers les nuages jusqu'au départ du trou 1, choix
// du départ si besoin, puis passage de relais au scroll (Timeline).
//
// États : 'idle' (écran d'accueil) -> 'flying' (descente) -> 'awaitingTee'
// (choix du départ) -> 'done' (visite guidée).
export default class IntroSequence {
    constructor(timeline) {
        this.experience = new Experience()
        this.timeline = timeline
        this.camera = this.experience.camera
        this.scene = this.experience.scene
        this.environment = this.experience.world.environment
        this.clouds = this.experience.world.clouds
        this.countryside = this.experience.world.countryside
        this.ui = this.experience.ui

        this.state = 'idle'
        this.elapsed = 0
        this.flight = null
        this.position = new THREE.Vector3()
        this.target = new THREE.Vector3()
        this.reducedMotion = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches ?? false

        this.startButton = document.querySelector('.start-button')
        this.skipButton = document.querySelector('.skip-intro')
        this.fadeElement = document.querySelector('.hole-fade')

        document.body.classList.add('intro-active')
        this.warmUp()
        this.bindEvents()
    }

    get isDone() {
        return this.state === 'done'
    }

    bindEvents() {
        this.onStart = () => this.start()
        this.onSkip = () => this.skip()

        this.startButton?.addEventListener('click', this.onStart)
        this.skipButton?.addEventListener('click', this.onSkip)

        if (this.startButton) {
            this.startButton.disabled = false
            this.startButton.classList.add('is-ready')
        }
    }

    // Compile dès maintenant les shaders des nuages et de la campagne,
    // pendant que l'écran d'accueil est affiché : au clic, ils sont
    // rendus dès la première image (sinon le terrain apparaît seul le
    // temps de la compilation).
    warmUp() {
        this.prepareFlight()
        this.countryside?.setOpacity(0.001)
        this.experience.renderer.instance.compile(this.scene, this.camera.instance)
        this.countryside?.setOpacity(0)
        if (this.clouds) this.clouds.group.visible = false
    }

    // La descente se pose sur la première image du plan du trou 1
    prepareFlight() {
        const firstShot = this.timeline.segments.find(segment => segment.hole === this.timeline.firstHole)
        const endPosition = firstShot.positionCurve.getPoint(0)
        const endTarget = firstShot.targetCurve.getPoint(0)

        this.flight = this.experience.course.buildFlyover(endPosition, endTarget)
        this.clouds?.show(this.flight.column)
    }

    start() {
        if (this.state !== 'idle') return

        this.prepareFlight()
        this.state = 'flying'
        this.elapsed = 0
        document.body.classList.add('intro-flying')

        this.ui?.hideIntroOverlay?.()
        this.experience.labels?.hideAll()
        this.experience.info?.clearHighlight()
        this.updateFlight(0)

        // Animations réduites : pas de descente, arrivée directe
        if (this.reducedMotion) this.skip({ fade: false })
    }

    // « Passer l'intro » : bref fondu au noir, la caméra est déjà posée
    // au départ du trou 1 quand l'image revient.
    skip({ fade = true } = {}) {
        if (this.state !== 'flying') return

        const land = () => {
            this.elapsed = this.flight.duration
            this.updateFlight(0)
        }

        if (!fade) {
            land()
            return
        }

        gsap.killTweensOf(this.fadeElement)
        gsap.to(this.fadeElement, {
            opacity: 1,
            duration: 0.35,
            ease: 'power2.in',
            onComplete: () => {
                land()
                gsap.to(this.fadeElement, { opacity: 0, duration: 0.6, ease: 'power2.out' })
            }
        })
    }

    // Descente : mouvement sinusoïdal (départ et arrivée très doux, aucune
    // accélération brusque), vitesse constante le long de la courbe.
    updateFlight(dt) {
        const flight = this.flight
        this.elapsed = Math.min(flight.duration, this.elapsed + dt)
        const t = this.elapsed / flight.duration
        const eased = 0.5 - 0.5 * Math.cos(Math.PI * t)
        const u = flight.positionCurve.getUtoTmapping(eased)

        flight.positionCurve.getPoint(u, this.position)
        flight.targetCurve.getPoint(u, this.target)

        const cam = this.camera.instance
        cam.position.copy(this.position)
        cam.lookAt(this.target)
        cam.fov = this.timeline.baseFov
        cam.updateProjectionMatrix()

        // Nuages pleinement présents dès la première image (l'écran
        // d'accueil s'efface par-dessus) ; la traversée les efface
        this.clouds?.update(cam)
        this.countryside?.setOpacity(1 - THREE.MathUtils.smoothstep(t, 0.8, 0.97))
        this.environment?.setFogOpening(1 - THREE.MathUtils.smoothstep(t, 0.5, 0.95))

        // Présentation du site pendant la descente
        this.experience.presentation?.setVisible(t > 0.1 && t < 0.72)

        if (t >= 1 && this.state === 'flying') {
            document.body.classList.remove('intro-flying')
            if (this.experience.teeSelection?.selectedColor) {
                this.finish()
            } else {
                this.state = 'awaitingTee'
                this.experience.teeSelection?.show()
            }
        }
    }

    update(dt) {
        if (this.state === 'flying') this.updateFlight(dt)
        else if (this.state === 'awaitingTee') this.updateFlight(0)
    }

    // La descente s'achève sur la première image du trou 1 : la timeline
    // prend la main sans coupure (fondu de pose si le départ a changé).
    finish() {
        if (this.state === 'done') return

        this.leaveIntro()
        this.timeline.beginTour(this.timeline.holesConfig[this.timeline.firstHole][0], { blendFromCurrent: true })
    }

    // Lien direct vers un trou : l'ouverture est sautée entièrement
    skipToHole(hole) {
        this.ui?.hideIntroOverlay?.({ immediate: true })
        this.leaveIntro()
        this.timeline.beginTour(this.timeline.getHoleEntryProgress(hole))
    }

    leaveIntro() {
        this.state = 'done'
        document.body.classList.remove('intro-active', 'intro-flying')
        this.experience.presentation?.setVisible(false)
        this.clouds?.hide()
        this.countryside?.setOpacity(0)
        this.environment?.setFogOpening(0)
    }

    destroy() {
        this.startButton?.removeEventListener('click', this.onStart)
        this.skipButton?.removeEventListener('click', this.onSkip)
        gsap.killTweensOf(this.fadeElement)
        this.experience = null
    }
}
